import { createContext, useContext, useEffect, useLayoutEffect } from "react";
import React, { useRef, useState} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

export const HoverTooltipContext = createContext({
    showTooltip: () => {},
    hideTooltip: () => {}
});

export const useHoverTooltip = () => useContext(HoverTooltipContext);

const TooltipNode = styled.div`
    position: fixed;
    z-index: 999999;
    pointer-events: none;
    padding: 0.4em;
    font-size: 0.8em;
    border-radius: 0.5em;
    border: 1px solid ${({ theme }) => `${theme.gray}40`};
    background: ${({ theme }) => theme.background || "#fff"};
    color: ${({ theme }) => theme.text || "#000"};
    box-shadow: 0px 3px 10px ${({ theme }) => theme.shadow || "rgba(0,0,0,0.2)"};
    transition: opacity 0.15s ease;
    white-space: ${({ wrap }) => (!wrap ? "normal" : wrap)};
    visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
    opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

function getPortalTargetNode() {
  	return document.fullscreenElement || document.body;
}

export const HoverTooltip = ({ children }) => {
    const tooltipRef = useRef(null);
    const [state, setState] = useState({
        text: "",
        x: 0,
        y: 0,
        wrap: null,
        visible: false,
        pending: null, // holds mouse coords between tooltip renderings 
        activeTrigger: null // tracks the triggering element/component 
    });

    const showTooltip = ({ text, x, y, maxWidth, wrap, trigger }) => {   
        setState(prev => {
            // no active trigger, allow update
            if (!prev.activeTrigger) {
            //console.log('no active trigger'); 
            return { ...prev, text, maxWidth, wrap: wrap, visible: true, pending: { x, y }, activeTrigger: trigger};
            }

            // same trigger, allow update 
            if (prev.activeTrigger === trigger) {
            //console.log('same active trigger'); 
            return {...prev, text, maxWidth, wrap: wrap, visible: true, pending: { x, y }};
            }

            // descendant of active trigger, takes priority so allow update
            if (prev.activeTrigger.contains(trigger)) {
            //console.log('descendant trigger allowed'); 
            return {...prev, text, maxWidth, wrap: wrap, visible: true, pending: { x, y }, activeTrigger: trigger };
            }

            // ancestor of active trigger, disallow update
            if (trigger.contains(prev.activeTrigger)) {
                //console.log('ancestor trigger disallowed'); 
            return prev;
            }

            // fallback to always update (perhaps overlapping siblings with tooltips, for example)
            // will allow so any conflicts are apparent in UI and likely caught during dev/QA
            //console.log('warning: fallback trigger'); 
            return {...prev, text, maxWidth, wrap: wrap, visible: true, pending: { x, y }, activeTrigger: trigger };
        });
    };

    const hideTooltip = (trigger, { force } = {}) => {
        if (!force && state.activeTrigger !== trigger) return;
        setState(prev => ({
            ...prev,
            visible: false,
            pending: null,
            activeTrigger: null
        }));
    };

    useEffect(() => {
        const dismiss = () => hideTooltip(null, { force: true });

        window.addEventListener('keydown', dismiss, true);
        window.addEventListener('scroll', dismiss, true);
        window.addEventListener('resize', dismiss);
        window.addEventListener('blur', dismiss);

        return () => {
            window.removeEventListener('keydown', dismiss, true);
            window.removeEventListener('scroll', dismiss, true);
            window.removeEventListener('resize', dismiss);
            window.removeEventListener('blur', dismiss);
        };
    }, []);


    useLayoutEffect(() => {  
        // Get mouse event coords via state.pending prop
        // Set top/left postion of tooltip relative to mouse coords
        // while adjusting for tooltip width and viewport boundaries as necessary

        if (!state.visible || !state.pending || !tooltipRef.current) return;

        const { x, y } = state.pending; 
        const t = tooltipRef.current;

        const tooltipWidth = t.offsetWidth;
        const tooltipHeight = t.offsetHeight;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 16;

        let left = x + margin;
        let top = y + margin;

        if (left + tooltipWidth + margin > viewportWidth) {
            left = viewportWidth - tooltipWidth - margin;
        }
        if (top + tooltipHeight + margin > viewportHeight) {
            top = viewportHeight - tooltipHeight - margin;
        }

        left = Math.max(margin, left);
        top = Math.max(margin, top);

        setState((prev) => ({
            ...prev,
            x: left,
            y: top,
            pending: null,
        }));
    }, [state.visible, state.pending]);

    useLayoutEffect(() => {
        if (
            state.visible &&
            state.activeTrigger &&
            !document.body.contains(state.activeTrigger)
        ) {
            setState(prev => ({
                ...prev,
                visible: false,
                pending: null,
                activeTrigger: null
            }));
        }
    }, [state.visible, state.activeTrigger]);

    return (
        <HoverTooltipContext.Provider value={{ showTooltip, hideTooltip }}>
            {children}
            {createPortal(
            <TooltipNode
                ref={tooltipRef}
                visible={state.visible}
                wrap={state.wrap}
                style={{
                top: state.y,
                left: state.x,
                maxWidth: state.wrap === 'nowrap' ? 'none' : `${state.maxWidth ?? 300}px`,
                }}
            >
                {state.text}
            </TooltipNode>,
                getPortalTargetNode()
            )}
        </HoverTooltipContext.Provider>
    );
};


